import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, userApi, iaApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing token on app load
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      loadUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async (token) => {
    try {
      setToken(token);
      localStorage.setItem('token', token);
      const response = await authApi.me();
      setUser(response.data);
      try {
        await iaApi.preSession({});
        sessionStorage.setItem('iron_ai_ok', '1');
      } catch {
        sessionStorage.removeItem('iron_ai_ok');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    const response = await authApi.me();
    setUser(response.data);
    return response.data;
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const normEmail =
        typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await authApi.login({ email: normEmail, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      try {
        await iaApi.preSession({});
        sessionStorage.setItem('iron_ai_ok', '1');
      } catch {
        sessionStorage.removeItem('iron_ai_ok');
      }
      return user;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, level = 'intermediario', goal = 'hipertrofia') => {
    try {
      setLoading(true);
      const normEmail =
        typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await authApi.register({
        name,
        email: normEmail,
        password,
        level,
        goal,
      });
      const { user } = response.data;
      
      // Auto login after register
      await login(normEmail, password);
      
      return user;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (data) => {
    try {
      const response = await userApi.update(user.id, data);
      setUser(response.data);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const updateApiKey = async (aiApiKey) => {
    try {
      const response = await userApi.updateApiKey(user.id, { aiApiKey });
      await refreshUser();
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    updateUser,
    updateApiKey,
    refreshUser,
    logout,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
